import cx from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useLocation } from 'react-router-dom';
import { urls } from 'services/_url';
import { Button } from 'app/shared/button';
import { Alert } from 'app/shared/form-alert';
import { Icon } from 'assets/icons';
import { isEmailValid } from 'app/shared/utils/input';
import { generateUniqueId } from 'app/shared/utils/general';
import { useGuestService } from 'hooks/guest';
import { useQuery } from 'app/hooks/query';
import styles from './signup.module.css';

const getStrength = (val) => {
  if (!val) return null;
  let score = 0;
  if (val.length >= 8) score++;
  if (val.length >= 12) score++;
  if (/[A-Z]/.test(val)) score++;
  if (/[0-9]/.test(val)) score++;
  if (/[^A-Za-z0-9]/.test(val)) score++;
  const levels = [
    { percent: 20, color: '#e53e3e', label: 'Weak' },
    { percent: 40, color: '#ed8936', label: 'Fair' },
    { percent: 60, color: '#ecc94b', label: 'Good' },
    { percent: 80, color: '#48bb78', label: 'Strong' },
    { percent: 100, color: '#03896c', label: 'Very strong' }
  ];
  return levels[Math.min(score - 1, 4)] || levels[0];
};

export const SignupForm = () => {
  const history = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const { resendActivationLink, signup } = useGuestService();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [alert, setAlert] = useState({});
  const [website, setWebsite] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [email_to_resend, setEmailToResend] = useState('');
  const [loading, setLoading] = useState(false);
  const [membership_data, setMembershipData] = useState({});
  const [password, setPassword] = useState('');
  const [resending, setResending] = useState(false);
  const [show_password, setShowPassword] = useState(false);
  const [show_resend_button, setShowResendButton] = useState(false);
  const [is_membership_acceptance, setIsMembershipAcceptance] = useState(false);
  const [errors, setErrors] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: ''
  });

  const clearForm = () => {
    setFirstname('');
    setLastname('');
    setEmail('');
    setPassword('');
  };

  useEffect(() => {
    const token = window.localStorage.getItem('acceptance');
    if (!token) return;

    const [user_email, tenant_id] = token.split(':');
    setIsMembershipAcceptance(!!token);
    setMembershipData({ user_email, tenant_id });
    handleEmailChange(user_email);
  }, []);

  useEffect(() => {
    const query_params = new URLSearchParams(location.search);
    const email_from_query = query_params.get('email');
    if (email_from_query) {
      setEmail(email_from_query);
    }
  }, [location.search]);

  const validateField = useCallback(
    (fieldName, value) => {
      let errorMessage = '';
      switch (fieldName) {
        case 'firstname':
          if (!value.trim()) {
            errorMessage = 'First name is required.';
          }
          break;
        case 'lastname':
          if (!value.trim()) {
            errorMessage = 'Last name is required.';
          }
          break;
        case 'email':
          if (!value.trim()) {
            errorMessage = 'Email address is required.';
          } else if (!isEmailValid(value)) {
            errorMessage = 'Invalid email address.';
          }
          break;
        case 'password':
          if (!value.trim()) {
            errorMessage = 'Password is required.';
          } else if (value.length < 8) {
            errorMessage = 'Password must be at least eight characters long.';
          }
          break;
        default:
          break;
      }
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: errorMessage }));
    },
    [errors]
  );

  const handleFirstnameChange = (value) => {
    setFirstname(value);
    validateField('firstname', value);
  };

  const handleLastnameChange = (value) => {
    setLastname(value);
    validateField('lastname', value);
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    validateField('email', value);
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    validateField('password', value);
  };

  const submitForm = async () => {
    if (website) return;

    if (disableSubmitButton) {
      validateField('firstname', firstname);
      validateField('lastname', lastname);
      validateField('email', email);
      validateField('password', password);
      return;
    }

    const recaptcha_token = executeRecaptcha ? await executeRecaptcha('agency_signup') : '';
    const utm_raw = window.localStorage.getItem('utm');
    let utm_data = {};
    try {
      utm_data = JSON.parse(utm_raw) || {};
    } catch (_) {
      utm_data = {};
    }
    const form_data = {
      account_type: 'agent',
      firstname,
      lastname,
      email,
      password,
      membership_data,
      recaptcha_token,
      utm_data
    };

    const data_to_submit = { ...form_data };

    setLoading(true);
    signup({ data: data_to_submit })
      .then((response) => {
        const { error, payload } = response;
        if (error) {
          setAlert({
            message: error,
            id: generateUniqueId()
          });
          return;
        }

        if (query.ref) {
          window.localStorage.setItem('ref', query.ref);
        }

        window.localStorage.removeItem('utm');
        window.localStorage.removeItem('acceptance');
        setEmailToResend(email);
        setShowResendButton(true);
        setAlert({
          message: payload,
          type: 'success',
          id: generateUniqueId()
        });
        clearForm();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toggleShowPassword = () => {
    setShowPassword(!show_password);
  };

  const handleResend = () => {
    setResending(true);
    resendActivationLink(email_to_resend)
      .then((response) => {
        const { error, payload } = response;
        if (error) {
          setAlert({
            message: error,
            id: generateUniqueId()
          });
          return;
        }
        setAlert({
          message: payload,
          type: 'success',
          id: generateUniqueId()
        });
      })
      .finally(() => setResending(false));
  };

  const isFormDisabled = (errors, values) => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    const isFieldsIncomplete = Object.values(values).some((value) => !value.trim());
    return hasErrors || isFieldsIncomplete;
  };

  const disableSubmitButton = isFormDisabled(errors, {
    firstname,
    lastname,
    email,
    password
  });

  const strength = getStrength(password);

  return (
    <div className={styles.splitWrapper}>
      {/* ── Form side ── */}
      <div className={styles.formSide}>
        <div className={styles.formInner}>
          <a className={styles.homeBtn} href={urls.landing} aria-label="Go to home">
            <Icon name="home" width={18} height={18} />
          </a>

          <div className={styles.formHeading}>Create an agency account</div>
          <div className={styles.formSubhead}>
            Already have an account?{' '}
            <button className={styles.subheadLink} onClick={() => history('/login')}>
              Log in →
            </button>
          </div>

          <form>
            <input
              type="text"
              name="hp_field"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="nope"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
            />

            <div className={styles.alertArea}>
              <Alert {...alert} />
            </div>

            {show_resend_button && (
              <div className={styles.resendLink}>
                {!resending ? (
                  <span onClick={handleResend}>Resend Link</span>
                ) : (
                  <span>Resending…</span>
                )}
              </div>
            )}

            {/* First + Last name row */}
            <div className={styles.nameRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>First name</label>
                <input
                  className={cx(styles.input, errors.firstname && styles.inputError)}
                  value={firstname}
                  onInput={(e) => handleFirstnameChange(e.target.value)}
                  onChange={() => {}}
                  placeholder="Ada"
                />
                {errors.firstname && <div className={styles.fieldError}>{errors.firstname}</div>}
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Last name</label>
                <input
                  className={cx(styles.input, errors.lastname && styles.inputError)}
                  value={lastname}
                  onInput={(e) => handleLastnameChange(e.target.value)}
                  onChange={() => {}}
                  placeholder="Obi"
                />
                {errors.lastname && <div className={styles.fieldError}>{errors.lastname}</div>}
              </div>
            </div>

            {/* Email */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Work email</label>
              <input
                type="email"
                className={cx(styles.input, errors.email && styles.inputError)}
                value={email}
                onInput={(e) => handleEmailChange(e.target.value)}
                onChange={() => {}}
                placeholder="ada@agency.com"
                autoComplete="email"
                disabled={is_membership_acceptance}
              />
              {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
            </div>

            {/* Password */}
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Password</label>
              <div className={styles.inputWrap}>
                <input
                  type={show_password ? 'text' : 'password'}
                  className={cx(
                    styles.input,
                    styles.inputPwPad,
                    errors.password && styles.inputError
                  )}
                  value={password}
                  onInput={(e) => handlePasswordChange(e.target.value)}
                  onChange={() => {}}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.pwToggle}
                  onClick={toggleShowPassword}
                  aria-label="Toggle password"
                >
                  <Icon name={show_password ? 'hide' : 'show'} width={18} height={18} />
                </button>
              </div>
              {password && strength && (
                <>
                  <div className={styles.strengthBar}>
                    <div
                      className={styles.strengthFill}
                      style={{ width: `${strength.percent}%`, background: strength.color }}
                    />
                  </div>
                  <div className={styles.strengthMeta}>
                    <span style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                </>
              )}
              {errors.password && <div className={styles.fieldError}>{errors.password}</div>}
            </div>

            <Button
              className={styles.submitBtn}
              onClick={submitForm}
              text="Create agency account →"
              loading={loading}
            />

            <div className={styles.termsNote}>
              By creating an account you agree to our <a href={urls.terms}>Terms of Service</a> and{' '}
              <a href={urls.policy}>Privacy Policy</a>.
            </div>
          </form>
        </div>
      </div>

      {/* ── Brand side ── */}
      <div className={styles.brandSide}>
        <div className={styles.brandBg} />
        <div className={styles.brandGrid} />
        <div className={styles.brandContent}>
          <h2 className={styles.brandHeadline}>
            Earn 10% recurring revenue.
            <br />
            <em>Forever.</em>
          </h2>
          <p className={styles.brandBody}>
            Onboard your clients to Go-Mailer and earn 10% of everything they pay — subscriptions,
            email volume, SMS, and usage — for the lifetime of the relationship.
          </p>

          <div className={styles.brandStats}>
            <div>
              <div className={styles.brandStatNum}>
                10<span className={styles.brandStatSymbol}>%</span>
              </div>
              <div className={styles.brandStatLbl}>Recurring revenue</div>
            </div>
            <div>
              <div className={styles.brandStatNum}>
                <span className={styles.brandStatSymbol}>₦</span>0
              </div>
              <div className={styles.brandStatLbl}>Upfront cost</div>
            </div>
            <div>
              <div className={styles.brandStatNum}>
                <span className={styles.brandStatSymbol}>∞</span>
              </div>
              <div className={styles.brandStatLbl}>Clients</div>
            </div>
          </div>

          <div className={styles.testimonial}>
            <div className={styles.testimonialStars}>★★★★★</div>
            <div className={styles.testimonialText}>
              "Go-Mailer stands out for their exceptional customer support and outstanding email
              deliverability. Their system delivers emails directly to the primary inbox 99% of the
              time."
            </div>
            <div className={styles.testimonialAuthor}>
              <div className={styles.testimonialAvatar}>G</div>
              <div>
                <div className={styles.testimonialName}>Gideon Oware</div>
                <div className={styles.testimonialRole}>Gideonoware.com</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
